import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Disable Supabase auth since we're using Firebase
    autoRefreshToken: false,
    persistSession: false,
    detectSessionInUrl: false
  }
})

// Enhanced logging operations
export const supabaseHelpers = {
  // Profile management helpers
  async getProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single()
      
      return { data, error }
    } catch (error) {
      console.error('Error fetching profile:', error)
      return { data: null, error }
    }
  },

  async createProfile(profileData) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .insert([profileData])
        .select()
        .single()
      
      return { data, error }
    } catch (error) {
      console.error('Error creating profile:', error)
      return { data: null, error }
    }
  },

  async updateProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .select()
        .single()
      
      return { data, error }
    } catch (error) {
      console.error('Error updating profile:', error)
      return { data: null, error }
    }
  },

  async getAllProfiles() {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })
      
      return { data: data || [], error }
    } catch (error) {
      console.error('Error fetching all profiles:', error)
      return { data: [], error }
    }
  },

  // Login Analytics Functions
  async getLoginAnalytics(days = 30) {
    try {
      const sinceDate = new Date()
      sinceDate.setDate(sinceDate.getDate() - days)
      
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, full_name, last_login_at, login_count, created_at')
        .gte('last_login_at', sinceDate.toISOString())
        .order('last_login_at', { ascending: false })
      
      return { data: data || [], error }
    } catch (error) {
      console.error('Error fetching login analytics:', error)
      return { data: [], error }
    }
  },

  async getActiveUsers(days = 7) {
    try {
      const sinceDate = new Date()
      sinceDate.setDate(sinceDate.getDate() - days)
      
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, full_name, last_login_at, login_count')
        .gte('last_login_at', sinceDate.toISOString())
        .order('last_login_at', { ascending: false })
      
      return { data: data || [], error }
    } catch (error) {
      console.error('Error fetching active users:', error)
      return { data: [], error }
    }
  },

  async getUserLoginStats(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, full_name, last_login_at, login_count, created_at')
        .eq('user_id', userId)
        .single()
      
      return { data, error }
    } catch (error) {
      console.error('Error fetching user login stats:', error)
      return { data: null, error }
    }
  },

  async getInactiveUsers(days = 30) {
    try {
      const sinceDate = new Date()
      sinceDate.setDate(sinceDate.getDate() - days)
      
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, full_name, last_login_at, login_count, created_at')
        .or(`last_login_at.is.null,last_login_at.lt.${sinceDate.toISOString()}`)
        .order('last_login_at', { ascending: true, nullsFirst: true })
      
      return { data: data || [], error }
    } catch (error) {
      console.error('Error fetching inactive users:', error)
      return { data: [], error }
    }
  },

  // Role Management Functions
  async changeUserRole(userId, newRole, changedByUserId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          user_role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single()
      
      if (!error) {
        // Log role change
        await this.logEvent('user_role_changed', {
          target_user_id: userId,
          new_role: newRole,
          changed_by: changedByUserId,
          timestamp: new Date().toISOString()
        }, changedByUserId, 'user_management', userId)
      }
      
      return { data, error }
    } catch (error) {
      console.error('Error changing user role:', error)
      return { data: null, error }
    }
  },

  async getUsersByRole(role) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, full_name, display_name, user_role, last_login_at, created_at')
        .eq('user_role', role)
        .order('created_at', { ascending: false })
      
      return { data: data || [], error }
    } catch (error) {
      console.error('Error fetching users by role:', error)
      return { data: [], error }
    }
  },

  async getAllUsersWithRoles() {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, full_name, display_name, user_role, last_login_at, login_count, created_at')
        .order('user_role', { ascending: false }) // Super admins first, then admins, then members
        .order('created_at', { ascending: false })
      
      return { data: data || [], error }
    } catch (error) {
      console.error('Error fetching all users with roles:', error)
      return { data: [], error }
    }
  },

  async getRoleStats() {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_role')
      
      if (error) return { data: null, error }
      
      const stats = data.reduce((acc, profile) => {
        const role = profile.user_role || 'member'
        acc[role] = (acc[role] || 0) + 1
        return acc
      }, {})
      
      return { data: stats, error: null }
    } catch (error) {
      console.error('Error fetching role stats:', error)
      return { data: null, error }
    }
  },

  // Optimized login handler (recommended for better performance)
  async handleGoogleLogin(userId, email, name, picture, givenName = null, familyName = null) {
    try {
      // Use Supabase RPC function if available, fallback to current method
      const { data, error } = await supabase
        .rpc('handle_google_login', {
          p_user_id: userId,
          p_email: email,
          p_name: name,
          p_picture: picture,
          p_given_name: givenName,
          p_family_name: familyName
        })
      
      if (error) {
        // Fallback to manual check-and-create if RPC function doesn't exist
        console.log('RPC function not found, using fallback method')
        return await this.handleGoogleLoginFallback(userId, email, name, picture, givenName, familyName)
      }
      
      return { 
        data: data.profile, 
        error: null,
        isNewUser: data.is_new_user 
      }
    } catch (error) {
      console.error('Error in handleGoogleLogin:', error)
      // Fallback to manual method
      return await this.handleGoogleLoginFallback(userId, email, name, picture, givenName, familyName)
    }
  },

  // Fallback method for systems without the database function
  async handleGoogleLoginFallback(userId, email, name, picture, givenName = null, familyName = null) {
    try {
      // Compute names with fallbacks
      const firstName = givenName || (name ? name.split(' ')[0] : null)
      const lastName = familyName || (name && name.includes(' ') ? name.substring(name.indexOf(' ') + 1) : null)
      const fullName = name || [firstName, lastName].filter(Boolean).join(' ')
      
      // Try to get existing profile
      const { data: existingProfile, error: profileError } = await this.getProfile(userId)
      
      if (existingProfile) {
        // Update last login time and increment login count
        const updates = {
          last_login_at: new Date().toISOString(),
          login_count: (existingProfile.login_count || 0) + 1
        }
        
        const { data: updatedProfile, error: updateError } = await this.updateProfile(userId, updates)
        
        // Log login event
        await this.logEvent('user_login', {
          user_id: userId,
          email: email,
          name: name,
          first_name: firstName,
          last_name: lastName,
          login_method: 'google_oauth_jwt',
          login_time: new Date().toISOString(),
          login_count: (existingProfile.login_count || 0) + 1,
          last_login_before: existingProfile.last_login_at
        })
        
        return { 
          data: updatedProfile || existingProfile, 
          error: updateError,
          isNewUser: false 
        }
      } else {
        // Create new profile (fallback - should match database function)
        const profileData = {
          user_id: userId,
          username: email?.split('@')[0] || null,
          first_name: firstName,
          last_name: lastName,
          full_name: fullName,
          display_name: firstName || email?.split('@')[0] || null,
          avatar_url: picture || null,
          email: email,
          user_role: 'tester',
          last_login_at: new Date().toISOString(),
          login_count: 1
        }

        const { data: newProfile, error: createError } = await this.createProfile(profileData)
        
        if (createError) {
          return { data: null, error: createError }
        }

        // Log registration event
        await this.logEvent('user_registered', {
          user_id: userId,
          email: email,
          name: name,
          first_name: firstName,
          last_name: lastName,
          registration_method: 'google_oauth_jwt',
          registration_time: new Date().toISOString(),
          login_count: 1
        })
        
        return { 
          data: newProfile, 
          error: null,
          isNewUser: true 
        }
      }
    } catch (error) {
      console.error('Error in handleGoogleLoginFallback:', error)
      return { data: null, error }
    }
  },

  async logEvent(event, details = {}, userId = null, entityType = null, entityId = null) {
    try {
      const { data, error } = await supabase
        .from('logs')
        .insert([{
          event,
          details,
          user_id: userId,
          related_entity_type: entityType,
          related_entity_id: entityId,
          created_at: new Date().toISOString()
        }])
        .select()
      
      if (error) throw error
      return { data: data[0], error: null }
    } catch (error) {
      console.error('Error logging event:', error)
      return { data: null, error }
    }
  },

  async getLogs(limit = 100) {
    try {
      const { data, error } = await supabase
        .from('logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error fetching logs:', error)
      return { data: null, error }
    }
  }
}

// Place helpers (FIXED)
export const placeHelpers = {
  async getOrCreatePlace(googlePlaceId) {
    try {
      // First try to get existing place
      const { data: existingPlace, error: fetchError } = await supabase
        .from('places')
        .select('*')
        .eq('google_place_id', googlePlaceId)
        .single();

      if (existingPlace) {
        return { data: existingPlace, error: null };
      }

      // If not found, create new place
      if (fetchError && fetchError.code === 'PGRST116') { // PGRST116 is "not found"
        const { data: newPlace, error: insertError } = await supabase
          .from('places')
          .insert([{ google_place_id: googlePlaceId }])
          .select()
          .single();

        if (insertError) {
          // Handle potential race condition - another process might have inserted it
          if (insertError.code === '23505') { // Unique constraint violation
            const { data: racePlace, error: raceError } = await supabase
              .from('places')
              .select('*')
              .eq('google_place_id', googlePlaceId)
              .single();
            
            return { data: racePlace, error: raceError };
          }
          throw insertError;
        }

        return { data: newPlace, error: null };
      }

      // If there was a different error, throw it
      throw fetchError;
    } catch (error) {
      console.error('Error creating/getting place:', error);
      return { data: null, error };
    }
  },

  async getPlaceByGoogleId(googlePlaceId) {
    try {
      const { data, error } = await supabase
        .from('places')
        .select('*')
        .eq('google_place_id', googlePlaceId)
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error fetching place:', error);
      return { data: null, error };
    }
  }
};

// Interest helpers with time-based expiration
export const interestHelpers = {
  async showInterest(userId, placeId, durationMinutes = 60, notes = null) {
    try {
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + durationMinutes);

      const { data, error } = await supabase
        .from('user_place_interests')
        .upsert([{
          user_id: userId,
          place_id: placeId,
          is_currently_interested: true,
          interest_expires_at: expiresAt.toISOString(),
          notes,
          updated_at: new Date().toISOString()
        }], { 
          onConflict: 'user_id,place_id',
          ignoreDuplicates: false 
        })
        .select();

      // Log the interest
      await supabaseHelpers.logEvent('user_showed_interest', {
        place_id: placeId,
        duration_minutes: durationMinutes,
        expires_at: expiresAt.toISOString()
      }, userId, 'place', placeId);

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error showing interest:', error);
      return { data: null, error };
    }
  },

  async removeInterest(userId, placeId) {
    try {
      const { data, error } = await supabase
        .from('user_place_interests')
        .update({
          is_currently_interested: false,
          interest_expires_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('place_id', placeId)
        .select();

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error removing interest:', error);
      return { data: null, error };
    }
  },

  async toggleFavorite(userId, placeId, isFavorite) {
    try {
      const { data, error } = await supabase
        .from('user_place_interests')
        .upsert([{
          user_id: userId,
          place_id: placeId,
          is_favorite: isFavorite,
          updated_at: new Date().toISOString()
        }], { 
          onConflict: 'user_id,place_id',
          ignoreDuplicates: false 
        })
        .select();

      await supabaseHelpers.logEvent('user_toggled_favorite', {
        place_id: placeId,
        is_favorite: isFavorite
      }, userId, 'place', placeId);

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return { data: null, error };
    }
  },

  async getActiveInterests(userId) {
    try {
      const { data, error } = await supabase
        .from('user_place_interests')
        .select('place_id, interest_expires_at, notes')
        .eq('user_id', userId)
        .eq('is_currently_interested', true)
        .gt('interest_expires_at', new Date().toISOString());

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching active interests:', error);
      return { data: [], error };
    }
  },

  async getUserFavorites(userId) {
    try {
      const { data, error } = await supabase
        .from('user_place_interests')
        .select('place_id')
        .eq('user_id', userId)
        .eq('is_favorite', true);

      return { data: data?.map(d => d.place_id) || [], error };
    } catch (error) {
      console.error('Error fetching favorites:', error);
      return { data: [], error };
    }
  },

  async getUsersInterestedInPlace(placeId) {
    try {
      const { data, error } = await supabase
        .from('user_place_interests')
        .select('user_id, interest_expires_at')
        .eq('place_id', placeId)
        .eq('is_currently_interested', true)
        .gt('interest_expires_at', new Date().toISOString());

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching interested users:', error);
      return { data: [], error };
    }
  },

  async cleanupExpiredInterests() {
    try {
      const { data, error } = await supabase
        .from('user_place_interests')
        .update({
          is_currently_interested: false,
          interest_expires_at: null
        })
        .lt('interest_expires_at', new Date().toISOString())
        .eq('is_currently_interested', true)
        .select();

      if (data && data.length > 0) {
        await supabaseHelpers.logEvent('expired_interests_cleaned', {
          count: data.length,
          cleaned_at: new Date().toISOString()
        });
      }

      return { data, error };
    } catch (error) {
      console.error('Error cleaning up expired interests:', error);
      return { data: null, error };
    }
  },

  async extendInterest(userId, placeId, additionalMinutes = 60) {
    try {
      const { data: current } = await supabase
        .from('user_place_interests')
        .select('interest_expires_at')
        .eq('user_id', userId)
        .eq('place_id', placeId)
        .eq('is_currently_interested', true)
        .single();

      if (current?.interest_expires_at) {
        const newExpiry = new Date(current.interest_expires_at);
        newExpiry.setMinutes(newExpiry.getMinutes() + additionalMinutes);

        const { data, error } = await supabase
          .from('user_place_interests')
          .update({
            interest_expires_at: newExpiry.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('place_id', placeId)
          .select();

        return { data: data?.[0], error };
      }

      return { data: null, error: 'No active interest found' };
    } catch (error) {
      console.error('Error extending interest:', error);
      return { data: null, error };
    }
  }
};

// Enhanced friendship helpers
export const friendshipHelpers = {
  async sendFriendRequest(requesterId, friendId) {
    try {
      // Check if friendship already exists
      const { data: existing } = await supabase
        .from('friendships')
        .select('*')
        .or(`and(requester_id.eq.${requesterId},friend_id.eq.${friendId}),and(requester_id.eq.${friendId},friend_id.eq.${requesterId})`)
        .single();

      if (existing) {
        return { data: null, error: 'Friendship already exists or request pending' };
      }

      // Get requester's profile information for the notification
      const { data: requesterProfile } = await supabase
        .from('profiles')
        .select('display_name, full_name, username')
        .eq('user_id', requesterId)
        .single();

      const { data, error } = await supabase
        .from('friendships')
        .insert([{
          requester_id: requesterId,
          friend_id: friendId,
          status: 'pending'
        }])
        .select();

      if (!error && data?.[0]) {
        // Create notification for the friend request recipient with sender's name
        const senderName = requesterProfile?.display_name || 
                          requesterProfile?.full_name || 
                          requesterProfile?.username || 
                          'Someone';
        
        await notificationHelpers.createNotification(
          friendId,
          'friend_request',
          'New Friend Request',
          `${senderName} wants to be your friend!`,
          data[0].id
        );

        await supabaseHelpers.logEvent('friend_request_sent', {
          friend_id: friendId
        }, requesterId, 'friendship', data[0].id);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error sending friend request:', error);
      return { data: null, error };
    }
  },

  async acceptFriendRequest(requesterId, friendId) {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .update({ 
          status: 'accepted'
        })
        .eq('requester_id', requesterId)
        .eq('friend_id', friendId)
        .eq('status', 'pending')
        .select();

      if (!error && data?.[0]) {
        // Get the friend's profile information for the notification
        const { data: friendProfile } = await supabase
          .from('profiles')
          .select('display_name, full_name, username')
          .eq('user_id', friendId)
          .single();

        const friendName = friendProfile?.display_name || 
                          friendProfile?.full_name || 
                          friendProfile?.username || 
                          'Someone';

        // Create notification for the requester when their request is accepted
        await notificationHelpers.createNotification(
          requesterId,
          'friend_request_accepted',
          'Friend Request Accepted',
          `${friendName} accepted your friend request! You can now coordinate on places to eat together.`,
          data[0].id
        );

        await supabaseHelpers.logEvent('friend_request_accepted', {
          requester_id: requesterId
        }, friendId, 'friendship', data[0].id);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error accepting friend request:', error);
      return { data: null, error };
    }
  },

  async rejectFriendRequest(requesterId, friendId) {
    try {
      // Delete the friendship record instead of updating status to 'rejected'
      // since the database constraint doesn't allow 'rejected' status
      const { data, error } = await supabase
        .from('friendships')
        .delete()
        .eq('requester_id', requesterId)
        .eq('friend_id', friendId)
        .eq('status', 'pending')
        .select();

      if (!error) {
        await supabaseHelpers.logEvent('friend_request_rejected', {
          requester_id: requesterId
        }, friendId, 'friendship', data?.[0]?.id);
      }

      // Note: We do NOT send a notification to the requester when their request is rejected
      // as per the user's requirement

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error rejecting friend request:', error);
      return { data: null, error };
    }
  },

  async getFriends(userId) {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .select(`
          *,
          requester:profiles!friendships_requester_id_fkey(user_id, username, full_name, display_name, avatar_url),
          friend:profiles!friendships_friend_id_fkey(user_id, username, full_name, display_name, avatar_url)
        `)
        .or(`requester_id.eq.${userId},friend_id.eq.${userId}`)
        .eq('status', 'accepted');

      if (error) return { data: null, error };

      const friends = data.map(f => {
        const friendProfile = f.requester_id === userId ? f.friend : f.requester;
        return {
          ...friendProfile,
          friendship_id: f.id,
          friendship_created_at: f.created_at
        };
      });

      return { data: friends, error: null };
    } catch (error) {
      console.error('Error fetching friends:', error);
      return { data: [], error };
    }
  },

  async getPendingRequests(userId) {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .select(`
          *,
          requester:profiles!friendships_requester_id_fkey(user_id, username, full_name, display_name, avatar_url)
        `)
        .eq('friend_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      return { data: [], error };
    }
  },

  async getFriendshipStatus(userId, otherUserId) {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .select('*')
        .or(`and(requester_id.eq.${userId},friend_id.eq.${otherUserId}),and(requester_id.eq.${otherUserId},friend_id.eq.${userId})`)
        .single();

      if (error && error.code !== 'PGRST116') {
        return { data: null, error };
      }

      if (!data) {
        return { data: { status: 'none' }, error: null };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error checking friendship status:', error);
      return { data: null, error };
    }
  },

  async getFriendshipById(friendshipId) {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .select('*')
        .eq('id', friendshipId)
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error fetching friendship by ID:', error);
      return { data: null, error };
    }
  },

  async removeFriend(userId, friendId) {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .delete()
        .or(`and(requester_id.eq.${userId},friend_id.eq.${friendId}),and(requester_id.eq.${friendId},friend_id.eq.${userId})`)
        .eq('status', 'accepted')
        .select();

      if (!error) {
        await supabaseHelpers.logEvent('friendship_removed', {
          friend_id: friendId
        }, userId, 'friendship', data?.[0]?.id);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error removing friend:', error);
      return { data: null, error };
    }
  }
};

// Meetup helpers
export const meetupHelpers = {
  async createMeetup(organizerId, placeId, title, description = null) {
    try {
      const { data, error } = await supabase
        .from('meetups')
        .insert([{
          organizer_id: organizerId,
          place_id: placeId,
          title,
          description
        }])
        .select();

      if (!error) {
        await supabaseHelpers.logEvent('meetup_created', {
          place_id: placeId,
          title: title
        }, organizerId, 'meetup', data[0].id);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error creating meetup:', error);
      return { data: null, error };
    }
  },

  async joinMeetup(meetupId, userId) {
    try {
      const { data, error } = await supabase
        .from('meetup_participants')
        .insert([{
          meetup_id: meetupId,
          user_id: userId
        }])
        .select();

      if (!error) {
        await supabaseHelpers.logEvent('user_joined_meetup', {
          meetup_id: meetupId
        }, userId, 'meetup', meetupId);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error joining meetup:', error);
      return { data: null, error };
    }
  },

  async getMeetupsByUser(userId) {
    try {
      const { data, error } = await supabase
        .from('meetups')
        .select('*')
        .eq('organizer_id', userId)
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching user meetups:', error);
      return { data: [], error };
    }
  },

  async getMeetupParticipants(meetupId) {
    try {
      const { data, error } = await supabase
        .from('meetup_participants')
        .select('user_id, joined_at')
        .eq('meetup_id', meetupId)
        .order('joined_at', { ascending: true });

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching meetup participants:', error);
      return { data: [], error };
    }
  }
};

// Message helpers
export const messageHelpers = {
  async sendDirectMessage(senderId, recipientId, content) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .insert([{
          sender_id: senderId,
          recipient_id: recipientId,
          content
        }])
        .select();

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error sending direct message:', error);
      return { data: null, error };
    }
  },

  async sendMeetupMessage(senderId, meetupId, content) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .insert([{
          sender_id: senderId,
          meetup_id: meetupId,
          content
        }])
        .select();

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error sending meetup message:', error);
      return { data: null, error };
    }
  },

  async getDirectMessages(userId, otherUserId) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${userId},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${userId})`)
        .order('created_at', { ascending: true });

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching direct messages:', error);
      return { data: [], error };
    }
  },

  async getMeetupMessages(meetupId) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('meetup_id', meetupId)
        .order('created_at', { ascending: true });

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching meetup messages:', error);
      return { data: [], error };
    }
  }
};

// Enhanced notification helpers
export const notificationHelpers = {
  async createNotification(userId, type, title, message, relatedId = null) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert([{
          user_id: userId,
          type,
          title,
          message,
          related_id: relatedId,
          is_read: false
        }])
        .select();

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error creating notification:', error);
      return { data: null, error };
    }
  },

  async getUserNotifications(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { data: [], error };
    }
  },

  async getUnreadNotificationCount(userId) {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      return { data: count || 0, error };
    } catch (error) {
      console.error('Error fetching unread notification count:', error);
      return { data: 0, error };
    }
  },

  async markNotificationRead(notificationId) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({ 
          is_read: true
        })
        .eq('id', notificationId)
        .select();

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error marking notification read:', error);
      return { data: null, error };
    }
  },

  async markAllNotificationsRead(userId) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({ 
          is_read: true
        })
        .eq('user_id', userId)
        .eq('is_read', false)
        .select();

      return { data: data || [], error };
    } catch (error) {
      console.error('Error marking all notifications read:', error);
      return { data: [], error };
    }
  },

  async deleteNotification(notificationId) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .select();

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { data: null, error };
    }
  }
};

// Feedback helpers for bug reports and feature requests
export const feedbackHelpers = {
  async submitFeedback(feedbackData) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .insert([{
          ...feedbackData,
          created_at: new Date().toISOString()
        }])
        .select();

      if (!error && data?.[0]) {
        // Log feedback submission
        await supabaseHelpers.logEvent('feedback_submitted', {
          feedback_id: data[0].id,
          type: feedbackData.type,
          priority: feedbackData.priority,
          title: feedbackData.title
        }, feedbackData.user_id, 'feedback', data[0].id);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error submitting feedback:', error);
      return { data: null, error };
    }
  },

  async getUserFeedback(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching user feedback:', error);
      return { data: [], error };
    }
  },

  async getAllFeedback(limit = 100, filters = {}) {
    try {
      let query = supabase
        .from('feedback')
        .select(`
          *,
          user:profiles!feedback_user_id_fkey(user_id, username, full_name, display_name, avatar_url),
          assigned_user:profiles!feedback_assigned_to_fkey(user_id, username, full_name, display_name)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      // Apply filters
      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to);
      }

      const { data, error } = await query;

      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching all feedback:', error);
      return { data: [], error };
    }
  },

  async updateFeedbackStatus(feedbackId, status, adminNotes = null, assignedTo = null, updatedBy = null) {
    try {
      const updates = {
        status,
        updated_at: new Date().toISOString()
      };

      if (adminNotes) updates.admin_notes = adminNotes;
      if (assignedTo) updates.assigned_to = assignedTo;
      if (status === 'resolved') updates.resolved_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('feedback')
        .update(updates)
        .eq('id', feedbackId)
        .select();

      if (!error && data?.[0]) {
        // Log status update
        await supabaseHelpers.logEvent('feedback_status_updated', {
          feedback_id: feedbackId,
          new_status: status,
          assigned_to: assignedTo,
          admin_notes: adminNotes ? 'Added' : null
        }, updatedBy, 'feedback', feedbackId);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error updating feedback status:', error);
      return { data: null, error };
    }
  },

  async getFeedbackStats() {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('type, status, priority');

      if (error) return { data: null, error };

      const stats = {
        total: data.length,
        byType: {},
        byStatus: {},
        byPriority: {}
      };

      data.forEach(item => {
        // Count by type
        stats.byType[item.type] = (stats.byType[item.type] || 0) + 1;
        
        // Count by status
        stats.byStatus[item.status] = (stats.byStatus[item.status] || 0) + 1;
        
        // Count by priority
        stats.byPriority[item.priority] = (stats.byPriority[item.priority] || 0) + 1;
      });

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching feedback stats:', error);
      return { data: null, error };
    }
  },

  async assignFeedback(feedbackId, assignedTo, assignedBy) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .update({
          assigned_to: assignedTo,
          status: 'in_progress',
          updated_at: new Date().toISOString()
        })
        .eq('id', feedbackId)
        .select();

      if (!error && data?.[0]) {
        // Log assignment
        await supabaseHelpers.logEvent('feedback_assigned', {
          feedback_id: feedbackId,
          assigned_to: assignedTo,
          assigned_by: assignedBy
        }, assignedBy, 'feedback', feedbackId);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error assigning feedback:', error);
      return { data: null, error };
    }
  },

  async addResolutionNotes(feedbackId, resolutionNotes, updatedBy) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .update({
          resolution_notes: resolutionNotes,
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', feedbackId)
        .select();

      if (!error && data?.[0]) {
        // Log resolution
        await supabaseHelpers.logEvent('feedback_resolved', {
          feedback_id: feedbackId,
          resolved_by: updatedBy
        }, updatedBy, 'feedback', feedbackId);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error adding resolution notes:', error);
      return { data: null, error };
    }
  }
};

// User interests helpers with GLOBAL privacy mode support
export const userInterestsHelpers = {
  // Get user's current privacy mode from profile or settings
  async getUserPrivacyMode(userId) {
    try {
      // First try to get from user_settings table
      const { data: settings, error: settingsError } = await supabase
        .from('user_settings')
        .select('privacy_mode, allowed_friends')
        .eq('user_id', userId)
        .single();

      if (settings && !settingsError) {
        return { 
          privacy_mode: settings.privacy_mode, 
          allowed_friends: settings.allowed_friends || [],
          error: null 
        };
      }

      // Fallback to profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('default_privacy_mode')
        .eq('user_id', userId)
        .single();

      if (profile && !profileError) {
        return { 
          privacy_mode: profile.default_privacy_mode || 'public', 
          allowed_friends: [],
          error: null 
        };
      }

      return { privacy_mode: 'public', allowed_friends: [], error: profileError };
    } catch (error) {
      console.error('Error getting user privacy mode:', error);
      return { privacy_mode: 'public', allowed_friends: [], error };
    }
  },

  // Update user's global privacy mode
  async updateUserPrivacyMode(userId, newPrivacyMode, allowedFriends = null) {
    try {
      // Try to update user_settings table first
      const { data: existingSettings, error: fetchError } = await supabase
        .from('user_settings')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (existingSettings && !fetchError) {
        // Update existing settings
        const updates = {
          privacy_mode: newPrivacyMode,
          updated_at: new Date().toISOString()
        };
        
        if (newPrivacyMode === 'certain_friends' && allowedFriends) {
          updates.allowed_friends = allowedFriends;
        } else if (newPrivacyMode !== 'certain_friends') {
          updates.allowed_friends = []; // Clear allowed friends for other modes
        }

        const { data, error } = await supabase
          .from('user_settings')
          .update(updates)
          .eq('user_id', userId)
          .select();

        return { data: data?.[0], error };
      } else {
        // Create new settings record (no existing record found)
        const settingsData = {
          user_id: userId,
          privacy_mode: newPrivacyMode,
          allowed_friends: (newPrivacyMode === 'certain_friends' && allowedFriends) ? allowedFriends : []
        };

        const { data, error } = await supabase
          .from('user_settings')
          .insert([settingsData])
          .select();

        if (!error && data?.[0]) {
          return { data: data[0], error: null };
        }

        // If insert failed, try fallback to profiles table
        if (error) {
          console.log('Failed to create user_settings, falling back to profiles table:', error);
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .update({ 
              default_privacy_mode: newPrivacyMode,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', userId)
            .select();

          return { data: profileData?.[0], error: profileError };
        }

        return { data: null, error };
      }
    } catch (error) {
      console.error('Error updating privacy mode:', error);
      // Fallback to updating profiles table
      try {
        const { data, error: profileError } = await supabase
          .from('profiles')
          .update({ 
            default_privacy_mode: newPrivacyMode,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .select();

        return { data: data?.[0], error: profileError };
      } catch (fallbackError) {
        console.error('Error updating privacy mode (fallback):', fallbackError);
        return { data: null, error: fallbackError };
      }
    }
  },

  // Add interest (uses existing user_place_interests table, no privacy fields needed)
  async addUserInterest(userId, placeData) {
    try {
      console.log('addUserInterest called with:', { userId, placeData });
      
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 60); // 60 minutes expiration

      const interestData = {
        user_id: userId,
        place_id: placeData.place_id,
        is_currently_interested: true,
        interest_expires_at: expiresAt.toISOString(),
        updated_at: new Date().toISOString(),
        // Optional: store place info for notifications (if columns exist)
        notes: `${placeData.place_name || ''} - ${placeData.place_address || ''}`.trim()
      };

      console.log('Inserting interest data:', interestData);

      const { data, error } = await supabase
        .from('user_place_interests')
        .upsert([interestData], { 
          onConflict: 'user_id,place_id',
          ignoreDuplicates: false 
        })
        .select();

      console.log('Supabase upsert result:', { data, error });

      if (!error && data?.[0]) {
        // Log interest creation
        await supabaseHelpers.logEvent('user_interest_added', {
          place_id: placeData.place_id,
          place_name: placeData.place_name,
          expires_at: expiresAt.toISOString()
        }, userId, 'user_interest', data[0].id);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error adding user interest:', error);
      return { data: null, error };
    }
  },

  // Remove interest (same as before)
  async removeUserInterest(userId, placeId) {
    try {
      const { data, error } = await supabase
        .from('user_place_interests')
        .update({
          is_currently_interested: false,
          interest_expires_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('place_id', placeId)
        .select();

      if (!error && data?.[0]) {
        await supabaseHelpers.logEvent('user_interest_removed', {
          place_id: placeId
        }, userId, 'user_interest', data[0].id);
      }

      return { data: data?.[0], error };
    } catch (error) {
      console.error('Error removing user interest:', error);
      return { data: null, error };
    }
  },

  // Get user's own interests (privacy doesn't matter for own interests)
  async getUserInterests(userId, includeExpired = false) {
    try {
      console.log('getUserInterests called with:', { userId, includeExpired });
      
      let query = supabase
        .from('user_place_interests')
        .select('*')
        .eq('user_id', userId)
        .eq('is_currently_interested', true)
        .order('updated_at', { ascending: false });

      if (!includeExpired) {
        const currentTime = new Date().toISOString();
        console.log('Filtering by expiration time, current time:', currentTime);
        query = query.gt('interest_expires_at', currentTime);
      }

      console.log('Executing query...');
      const { data, error } = await query;
      console.log('getUserInterests query result:', { data, error, count: data?.length });
      
      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching user interests:', error);
      return { data: [], error };
    }
  },

  // Get visible interests for a place (respects global privacy settings)
  async getVisibleInterestsForPlace(currentUserId, placeId) {
    try {
      // Get all active interests for this place
      const { data: allInterests, error } = await supabase
        .from('user_place_interests')
        .select(`
          *,
          user:profiles!user_place_interests_user_id_fkey(user_id, username, full_name, display_name, avatar_url)
        `)
        .eq('place_id', placeId)
        .eq('is_currently_interested', true)
        .gt('interest_expires_at', new Date().toISOString());

      if (error) return { data: [], error };

      // Filter based on each user's privacy settings
      const visibleInterests = [];
      
      for (const interest of allInterests) {
        // Always show own interests
        if (interest.user_id === currentUserId) {
          visibleInterests.push(interest);
          continue;
        }

        // Get the interest owner's privacy mode
        const { privacy_mode, allowed_friends } = await this.getUserPrivacyMode(interest.user_id);

        if (privacy_mode === 'public') {
          visibleInterests.push(interest);
        } else if (privacy_mode === 'all_friends') {
          // Check if current user is friends with interest owner
          const { data: friendship } = await friendshipHelpers.getFriendshipStatus(currentUserId, interest.user_id);
          if (friendship && friendship.status === 'accepted') {
            visibleInterests.push(interest);
          }
        } else if (privacy_mode === 'certain_friends') {
          // Check if current user is in the allowed friends list
          if (allowed_friends && allowed_friends.includes(currentUserId)) {
            visibleInterests.push(interest);
          }
        }
      }

      return { data: visibleInterests, error: null };
    } catch (error) {
      console.error('Error fetching visible interests:', error);
      return { data: [], error };
    }
  },

  // Get all visible interests (for discovery/social features)
  async getAllVisibleInterests(currentUserId) {
    try {
      // Get all active interests
      const { data: allInterests, error } = await supabase
        .from('user_place_interests')
        .select(`
          *,
          user:profiles!user_place_interests_user_id_fkey(user_id, username, full_name, display_name, avatar_url)
        `)
        .eq('is_currently_interested', true)
        .gt('interest_expires_at', new Date().toISOString())
        .order('updated_at', { ascending: false });

      if (error) return { data: [], error };

      // Filter based on privacy (same logic as above)
      const visibleInterests = [];
      
      for (const interest of allInterests) {
        if (interest.user_id === currentUserId) {
          visibleInterests.push(interest);
          continue;
        }

        const { privacy_mode, allowed_friends } = await this.getUserPrivacyMode(interest.user_id);

        if (privacy_mode === 'public') {
          visibleInterests.push(interest);
        } else if (privacy_mode === 'all_friends') {
          const { data: friendship } = await friendshipHelpers.getFriendshipStatus(currentUserId, interest.user_id);
          if (friendship && friendship.status === 'accepted') {
            visibleInterests.push(interest);
          }
        } else if (privacy_mode === 'certain_friends') {
          if (allowed_friends && allowed_friends.includes(currentUserId)) {
            visibleInterests.push(interest);
          }
        }
      }

      return { data: visibleInterests, error: null };
    } catch (error) {
      console.error('Error fetching all visible interests:', error);
      return { data: [], error };
    }
  },

  // Cleanup expired interests (same as before)
  async cleanupExpiredInterests() {
    try {
      const { data, error } = await supabase
        .from('user_place_interests')
        .update({
          is_currently_interested: false,
          interest_expires_at: null
        })
        .lt('interest_expires_at', new Date().toISOString())
        .eq('is_currently_interested', true)
        .select();

      if (!error && data && data.length > 0) {
        await supabaseHelpers.logEvent('expired_interests_cleaned', {
          count: data.length,
          cleaned_at: new Date().toISOString()
        });

        // Send expiration notifications
        for (const interest of data) {
          const placeName = interest.notes ? interest.notes.split(' - ')[0] : 'a location';
          await notificationHelpers.createNotification(
            interest.user_id,
            'interest_expired',
            'Location Interest Expired',
            `Your interest in ${placeName} has expired.`,
            interest.id
          );
        }
      }

      return { data: data || [], error };
    } catch (error) {
      console.error('Error cleaning up expired interests:', error);
      return { data: [], error };
    }
  }
};

// Background cleanup
export const backgroundCleanup = {
  async runCleanup() {
    console.log('Running background cleanup...');
    
    const { data: expired } = await userInterestsHelpers.cleanupExpiredInterests();
    
    console.log(`Cleanup complete: ${expired?.length || 0} expired interests cleaned`);
  },

  startPeriodicCleanup() {
    // Run cleanup every 5 minutes
    setInterval(this.runCleanup, 5 * 60 * 1000);
  }
}; 